import {
  collection,
  onSnapshot,
  query,
  where,
  limit,
  orderBy,
  startAfter,
} from "firebase/firestore";
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { auth, db } from "../../firebaseConfig";
import { Row, Col, Typography, Spin, Button, Empty, Card, Badge, Avatar } from "antd";
import { UserOutlined, LoadingOutlined } from "@ant-design/icons";
import { useAuthState } from "react-firebase-hooks/auth";
import { useUserData } from "../../hooks/useUsers";
import { getAge, getGunaPoints } from "../../lib/utils";

const { Meta } = Card;
const { Title, Text } = Typography;

function getDate(years) {
  var date = new Date();
  date.setFullYear(date.getFullYear() - years);
  return date;
}

export default function Profiles(params) {
  const [loading, setLoading] = useState(true);
  const [loadMoreLoading, setLoadMoreLoading] = useState(false);
  const [profileCount, setProfileCount] = useState(0);
  const [lastProfile, setLastProfile] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [user] = useAuthState(auth);
  const { userData } = useUserData(user?.uid);

  function queryParams(q, params) {
    if (params.searchCriteria.maritalStatus)
      q = query(
        q,
        where("maritalStatus", "==", params.searchCriteria.maritalStatus),
        orderBy("maritalStatus")
      );
    if (params.searchCriteria.subCaste)
      q = query(
        q,
        where("subCaste", "==", params.searchCriteria.subCaste),
        orderBy("subCaste")
      );
    if (params.searchCriteria.nakshatram)
      q = query(
        q,
        where("nakshatram", "==", params.searchCriteria.nakshatram),
        orderBy("nakshatram")
      );
    if (params.searchCriteria.haveKids)
      q = query(
        q,
        where("haveKids", "==", params.searchCriteria.haveKids),
        orderBy("haveKids")
      );
    if (params.searchCriteria.wantKids)
      q = query(
        q,
        where("wantKids", "==", params.searchCriteria.wantKids),
        orderBy("wantKids")
      );
    if (params.searchCriteria.motherTongue)
      q = query(
        q,
        where("motherTongue", "==", params.searchCriteria.motherTongue),
        orderBy("motherTongue")
      );
    if (params.searchCriteria.education)
      q = query(
        q,
        where("education", "==", params.searchCriteria.education),
        orderBy("education")
      );
    if (params.searchCriteria.employment)
      q = query(
        q,
        where("employment", "==", params.searchCriteria.employment),
        orderBy("employment")
      );
    if (params.searchCriteria.smoking)
      q = query(
        q,
        where("smoking", "==", params.searchCriteria.smoking),
        orderBy("smoking")
      );
    if (params.searchCriteria.drinking)
      q = query(
        q,
        where("drinking", "==", params.searchCriteria.drinking),
        orderBy("drinking")
      );
    if (params.searchCriteria.minAge)
      q = query(
        q,
        where("dateOfBirth", "<=", getDate(params.searchCriteria.minAge))
      );
    if (params.searchCriteria.maxAge)
      q = query(
        q,
        where("dateOfBirth", ">=", getDate(params.searchCriteria.maxAge + 1))
      );

    return q;
  }

  useEffect(() => {
    if (userData) {
      const getOppositeGender = userData?.gender === "Male" ? "Female" : "Male";

      const profileRef = collection(db, "users");
      let q = query(
        profileRef,
        where("profileCompleted", "==", true),
        where("gender", "==", getOppositeGender),
        orderBy("dateOfBirth", "desc"),
        limit(10)
      );

      q = queryParams(q, params);

      onSnapshot(q, (snapshot) => {
        const profiles = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        const lastProfile = snapshot.docs[snapshot.docs.length - 1];
        setLastProfile(lastProfile);
        setProfiles(profiles);
        setLoading(false);
      });
    }
  }, [params, userData, user]);

  const loadMoreProfiles = () => {
    setLoadMoreLoading(true);
    const getOppositeGender = userData?.gender === "Male" ? "Female" : "Male";

    const profileRef = collection(db, "users");

    let q = query(
      profileRef,
      where("profileCompleted", "==", true),
      where("gender", "==", getOppositeGender),
      orderBy("dateOfBirth", "desc"),
      limit(10),
      startAfter(lastProfile)
    );

    q = queryParams(q, params);

    onSnapshot(q, (snapshot) => {
      const moreProfiles = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      const lastProfile = snapshot.docs[snapshot.docs.length - 1];
      setLastProfile(lastProfile);
      profiles.push(...moreProfiles);
      setProfileCount(moreProfiles.length);
      setLoadMoreLoading(false);
    });
  };

  const antIcon = <LoadingOutlined style={{ fontSize: 24, color: '#667eea' }} spin />;

  return (
    <div style={{ padding: '20px' }}>
      {/* Header */}
      <div style={{ marginBottom: '30px', textAlign: 'center' }}>
        <Title 
          level={2} 
          style={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: '8px',
            fontWeight: '700'
          }}
        >
          Discover Profiles
        </Title>
        <Text type="secondary" style={{ fontSize: '16px' }}>
          Find your perfect match from our curated profiles
        </Text>
      </div>

      {/* Loading State */}
      {loading && (
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <Spin indicator={antIcon} size="large" />
          <div style={{ marginTop: '20px' }}>
            <Text style={{ fontSize: '16px', color: '#667eea' }}>Loading profiles...</Text>
          </div>
        </div>
      )}

      {/* Profiles Grid */}
      {profiles && profiles.length > 0 && (
        <Row gutter={[24, 24]}>
          {profiles
            .filter((item) => item.userId !== user.uid)
            .map((profile) => (
              <Col
                key={profile.id}
                xs={24}
                sm={12}
                lg={8}
                xl={6}
              >
                <Link to={`/profile-page/${profile.id}`}>
                  <Badge.Ribbon
                    text={`${getAge(profile.dateOfBirth)} yrs`}
                    style={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      fontSize: '12px',
                      fontWeight: '600'
                    }}
                  >
                    <Card
                      hoverable
                      className="profile-card"
                      style={{
                        borderRadius: '16px',
                        overflow: 'hidden',
                        border: 'none',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                        transition: 'all 0.3s ease',
                        background: 'rgba(255, 255, 255, 0.95)',
                        backdropFilter: 'blur(10px)'
                      }}
                      cover={
                        <div 
                          style={{ 
                            height: '280px', 
                            overflow: 'hidden',
                            position: 'relative'
                          }}
                        >
                          {profile.profileImages && profile.profileImages[0]?.url ? (
                            <img
                              alt={profile.firstName}
                              style={{ 
                                height: '100%', 
                                width: '100%',
                                objectFit: 'cover',
                                transition: 'transform 0.3s ease'
                              }}
                              src={profile.profileImages[0]?.url}
                              onMouseOver={(e) => e.target.style.transform = 'scale(1.05)'}
                              onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
                            />
                          ) : (
                            <div 
                              style={{
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                color: 'white'
                              }}
                            >
                              <Avatar size={80} icon={<UserOutlined />} style={{ backgroundColor: 'transparent' }} />
                            </div>
                          )}
                        </div>
                      }
                    >
                      <Meta
                        title={
                          <Text 
                            style={{ 
                              fontSize: '18px', 
                              fontWeight: '600',
                              color: '#2c3e50'
                            }}
                          >
                            {`${profile.firstName} ${profile.lastName}`}
                          </Text>
                        }
                        description={
                          <div style={{ marginTop: '8px' }}>
                            <Text 
                              type="secondary" 
                              style={{ 
                                fontSize: '14px',
                                lineHeight: '1.6'
                              }}
                            >
                              {profile.gender} • {profile.maritalStatus} • {profile.subCaste}
                              <br />
                              {profile.education} • {profile.currentResidence}
                            </Text>
                          </div>
                        }
                      />
                      
                      {/* Guna Points */}
                      <div 
                        style={{
                          marginTop: '16px',
                          padding: '12px',
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          borderRadius: '8px',
                          textAlign: 'center'
                        }}
                      >
                        <Text 
                          style={{ 
                            color: 'white',
                            fontSize: '14px',
                            fontWeight: '600'
                          }}
                        >
                          Guna Points: {" "}
                          {getGunaPoints(
                            userData?.gender === "Female"
                              ? userData?.nakshatram +
                                  "|" +
                                  userData?.padam +
                                  "|" +
                                  profile?.nakshatram +
                                  "|" +
                                  profile?.padam
                              : profile?.nakshatram +
                                  "|" +
                                  profile?.padam +
                                  "|" +
                                  userData?.nakshatram +
                                  "|" +
                                  userData?.padam
                          )}
                        </Text>
                      </div>
                    </Card>
                  </Badge.Ribbon>
                </Link>
              </Col>
            ))}
        </Row>
      )}

      {/* Load More Button */}
      {profiles.length > 0 && (
        <div style={{ textAlign: "center", marginTop: "40px" }}>
          <Button
            type="primary"
            size="large"
            disabled={profileCount < 6}
            loading={loadMoreLoading}
            onClick={loadMoreProfiles}
            style={{
              borderRadius: '8px',
              height: '45px',
              minWidth: '160px',
              fontWeight: '600',
              background: profileCount < 6 
                ? '#f5f5f5' 
                : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              border: 'none',
              boxShadow: profileCount < 6 
                ? 'none' 
                : '0 4px 15px rgba(102, 126, 234, 0.4)'
            }}
          >
            Load More Profiles
          </Button>
        </div>
      )}

      {/* Empty State */}
      {profiles && profiles.length === 0 && !loading && (
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            imageStyle={{
              height: 120,
            }}
            description={
              <div>
                <Text 
                  style={{ 
                    fontSize: '18px',
                    color: '#8c8c8c',
                    fontWeight: '500'
                  }}
                >
                  No profiles found
                </Text>
                <br />
                <Text type="secondary" style={{ fontSize: '14px' }}>
                  Try adjusting your search criteria to find more matches
                </Text>
              </div>
            }
          />
        </div>
      )}

      <style jsx>{`
        .profile-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15) !important;
        }
      `}</style>
    </div>
  );
}